"use client";

import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";

import {
  ArrowRight,
  Camera,
  FileText,
  Upload,
  Loader2,
  Pencil,
} from "lucide-react";

import { Excalidraw, exportToBlob, MIME_TYPES } from "@excalidraw/excalidraw";

import { getDefaultAppState } from "@excalidraw/excalidraw/appState";

import { getDataURL } from "@excalidraw/excalidraw/data/blob";

import { v4 as uuidv4 } from "uuid";

import CameraCapture from "@excalidraw/excalidraw/components/CameraCapture";

import type {
  NonDeletedExcalidrawElement,
  ExcalidrawElement,
} from "@excalidraw/element/types";

import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { PageLayout } from "../components/PageLayout";

import { ImageUploader } from "../components/excalidraw/ImageUploader";
import { processImage } from "../lib/processImage";

import { upsertDiagram } from "../data/diagrams";

// —— 先に宣言（function 宣言は hoist される）——
async function maybeSaveDiagram(elements: NonDeletedExcalidrawElement[]) {
  if (!window.confirm("Save this diagram to your library?")) {
    return;
  }
  const name =
    window.prompt("Enter diagram name") ||
    `Diagram ${new Date().toLocaleString()}`;
  const appState = getDefaultAppState();
  const blob = await exportToBlob({
    elements,
    appState: {
      ...appState,
      exportBackground: true,
      viewBackgroundColor: appState.viewBackgroundColor,
    },
    files: {},
    mimeType: MIME_TYPES.png,
  });
  const thumbnail = await getDataURL(blob);
  upsertDiagram({
    id: uuidv4(),
    name,
    elements,
    appState: {
      ...appState,
      width: window.innerWidth,
      height: window.innerHeight,
      offsetTop: 0,
      offsetLeft: 0,
    },
    thumbnail,
  });
}

export default function CapturePage() {
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [shapes, setShapes] = useState<NonDeletedExcalidrawElement[]>([]);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [imageSizeRatio, setImageSizeRatio] = useState(1);

  const handleCapture = (dataUrl: string) => {
    setCapturedImage(dataUrl);
    setImageUrl(dataUrl);
  };

  const handleUpload = (url: string) => {
    const img = new Image();
    img.onload = () => {
      setImageSizeRatio(img.width / img.height);
      setImageUrl(url);
      setShapes([]);
      setError(null);
    };
    img.onerror = () => {
      // 失敗してもURLは反映し、状態をリセット
      setImageUrl(url);
      setShapes([]);
      setError(null);
    };
    img.src = url;
  };

  const handleProcess = async () => {
    if (!imageUrl) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const result = await processImage(imageUrl);

      if (result.length === 0) {
        setError(
          "No shapes were detected in the image. Try a different image or add shapes manually.",
        );
        setShapes([]);
      } else {
        setShapes(result);
        await maybeSaveDiagram(result);
      }
    } catch (err) {
      console.error("Error processing image:", err);
      setError(
        "Failed to process image. The AI service may be temporarily unavailable. You can still add shapes manually.",
      );
      setShapes([]); // 混乱を避けるためクリア
    } finally {
      setIsProcessing(false);
    }
  };

  // SSR/ビルド時に window 参照で落ちへんようにガード
  const vw = typeof window !== "undefined" ? window.innerWidth : 0;

  return (
    <PageLayout>
      <div className="md:max-w-5xl w-full space-y-4">
        <div className="flex justify-center">
          <Link to="/editor" className="w-full">
            <Button size="lg" className="mb-4 w-full">
              Proceed to blank
              <ArrowRight className="h-4 w-4 mr-2" />
            </Button>
          </Link>
        </div>

        <Tabs defaultValue="capture" className="w-full">
          {/* Capture / Upload 切替 */}
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="capture" className="flex items-center gap-2">
              <Camera className="h-4 w-4" />
              Capture
            </TabsTrigger>
            <TabsTrigger value="upload" className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Upload
            </TabsTrigger>
          </TabsList>

          {/* Capture */}
          <TabsContent value="capture" className="space-y-6">
            <Card>
              <CardContent className="p-6 space-y-4">
                {capturedImage ? (
                  <div className="relative aspect-video bg-muted rounded-md overflow-hidden">
                    <img
                      src={capturedImage || "/placeholder.svg"}
                      alt="Captured"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <CameraCapture onCapture={handleCapture} />
                )}

                {capturedImage && (
                  <div className="flex gap-4">
                    <Button
                      size="lg"
                      className="w-full"
                      variant="outline"
                      onClick={() => setCapturedImage(null)}
                    >
                      Retake
                    </Button>
                    <Button
                      size="lg"
                      className="w-full"
                      onClick={handleProcess}
                      disabled={isProcessing}
                    >
                      {isProcessing ? (
                        <>Processing...</>
                      ) : (
                        <>
                          <FileText className="h-4 w-4 mr-2" />
                          Convert to Document
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Upload */}
          <TabsContent value="upload" className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <ImageUploader onImageUpload={handleUpload} />
                {imageUrl && (
                  <div className="mt-4">
                    <Button
                      onClick={handleProcess}
                      disabled={isProcessing}
                      className="w-full"
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        "Process Image with AI"
                      )}
                    </Button>
                  </div>
                )}

                {error && (
                  <div className="mt-4 p-3 bg-red-100 text-red-700 rounded">
                    {error}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Canvas Preview */}
        <div className="lg:col-span-2">
          <div className="bg-white p-6 rounded-lg shadow h-full flex flex-col items-center justify-center">
            <h2 className="text-xl font-semibold mb-4">Canvas Preview</h2>
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isProcessing && (
                <div className="absolute inset-0 flex items-center justify-center z-10">
                  {/* Foreground */}
                  <div className="absolute inset-0 bg-white opacity-50 rounded-lg" />
                  {/* Spinner */}
                  <Loader2 className="h-32 w-32 animate-spin text-muted-foreground" />
                </div>
              )}

              {shapes && shapes.filter(Boolean).length > 0 && (
                <div
                  style={{
                    width: vw ? `${vw * 0.85}px` : "85vw",
                    height: vw ? `${(vw * 1.25) / imageSizeRatio}px` : "85vh",
                    position: "relative",
                  }}
                >
                  <Excalidraw
                    key={JSON.stringify(shapes.map((s) => s.id))}
                    initialData={{
                      elements:
                        (shapes.filter(Boolean) as ExcalidrawElement[]) || [],
                      appState: {
                        gridModeEnabled: true,
                        activeTool: {
                          type: "hand",
                          customType: null,
                          locked: true,
                          lastActiveTool: {
                            type: "hand",
                            customType: null,
                          },
                          fromSelection: false,
                        },
                      },
                    }}
                  />
                </div>
              )}
            </div>

            <div className="flex justify-center mt-4 w-full">
              <Button
                size="lg"
                className="w-full"
                disabled={shapes.length === 0}
                onClick={() => {
                  // Editor 用に shapes を保存し、state 経由でも渡す
                  try {
                    localStorage.setItem("shapes", JSON.stringify(shapes));
                  } catch (e) {
                    console.error("Failed to save shapes for editor:", e);
                  }
                  navigate("/editor", { state: { shapes } });
                }}
              >
                <Pencil className="h-5 w-5 mr-2" />
                Proceed to Editor
              </Button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-muted/30 rounded-lg p-6">
          <h2 className="text-lg font-medium mb-2">Tips for Best Results</h2>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li>Ensure good lighting to improve recognition accuracy</li>
            <li>Position the document to fill the frame</li>
            <li>Avoid shadows and glare on the document</li>
            <li>Keep the camera steady when capturing</li>
            <li>For text documents, ensure text is clearly visible</li>
          </ul>
        </footer>
      </div>
    </PageLayout>
  );
}
